const PictViewClass = require('pict-view');

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

class DocumentSearch extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		this.DefaultSearchFilters = {
			IDCustomer: null,
			Projects: [],
			Materials: [],
			MixDesigns: [],
			StartDate: '',
			EndDate: '',
			PageSize: 25,
			CurrentPage: 1,
			TotalPages: 1,
			SortBy: 'DocumentDate',
			SortDirection: 'DESC',
		};
		this.CurrentSearchFilters = JSON.parse(JSON.stringify(this.DefaultSearchFilters));
		this.CurrentSearchFiltersCount = 0;
		this.CurrentSearchResults = [];
		// this is the active Document that is selected; will be one we see in the "Document Details" panel, etc.
		this.selectedDocument = {};
		this.mode = 'none';
		this.documentSearchResultsCallback = (results) =>
		{};
	}

	setMode(newMode)
	{
		this.mode = newMode;
	}

	onBeforeRenderAsync(pCallback)
	{
		if (this.pict.views.HeadlightPaneDocuments.Intent?.type === 'add')
		{
			this.mode = 'add-with-intent';
		}
		this.CurrentSearchFilters.IDCustomer = this.AppData?.DocumentData?.ReportData?.IDCustomer || this.AppData?.CurrentUser?.CustomerID || 0;
		if (pCallback)
		{
			pCallback();
		};
	}

	onAfterRenderAsync(pCallback)
	{
		// PROJECT Selector Filter
		// if we have a project already, make it sticky on the top of the list
		const tmpProject = this.AppData?.DocumentData?.ReportData?.IDProject ? { id: this.AppData?.DocumentData?.ReportData?.IDProject, text: this.AppData?.DocumentData?.ReportData?.Project?.Name } : null;
		this.initializeSelect2Plugin({
			dropdownCssClass: 'theme-dark' + (tmpProject ? ' has-sticky-option' : ''),
			placeholder: 'Search by projects...',
			ElementID: 'DocumentSearch-Projects',
			ParentID: 'DocumentSearch-SearchEntry',
			URL: 'Projects',
			IDField: 'IDProject',
			TextField: 'Name',
			StickyOption: tmpProject,
			PreselectOption: tmpProject,
		}, () =>
		{
			this.log.info('Select2 Projects initialized');
		});

		// User Selector Filter
		// if we have a user already (we always should...), make them sticky on the top of the list
		const tmpUser = this.AppData?.CurrentUser?.UserID ? { id: this.AppData?.CurrentUser?.UserID, text: this.AppData?.CurrentUser?.NameFirst + ' ' + this.AppData?.CurrentUser?.NameLast } : null;
		this.initializeSelect2Plugin({
			dropdownCssClass: 'theme-dark' + (tmpUser ? ' has-sticky-option' : ''),
			placeholder: 'Search by users...',
			ElementID: 'DocumentSearch-Users',
			ParentID: 'DocumentSearch-SearchEntry',
			URL: 'Users',
			IDField: 'IDUser',
			TextField: 'NameFirst',
			StickyOption: tmpUser,
			minimumInputLength: 0,
			formatMethod: (pResults) =>
			{
				return pResults.map((pResult) =>
				{
					return {
						IDUser: pResult.IDUser,
						NameFirst: `${pResult.NameFirst} ${pResult.NameLast}`,
					};
				});
			},
		}, () =>
		{
			this.log.info('Select2 Users initialized');
		});

		// START DATE date picker
		window.flatpickr('#DocumentSearch-StartDate', {
			dateFormat: 'Y-m-d',
			onReady: function(dateObj, dateStr, instance)
			{
				_Pict.providers.UtilityProvider.buildFlatpickrExtraButtons(instance, 'DocumentSearch-StartDate');
			},
			onChange: function(selectedDates, dateStr, instance)
			{
				// a date was selected, update the End Date picker to disable dates before the start date
				const siblingElement = document.getElementById('DocumentSearch-EndDate');
				siblingElement['_flatpickr'].config.minDate = dateStr ? dateStr : null;
				siblingElement['_flatpickr'].redraw();
			},
		});

		// END DATE date picker
		window.flatpickr('#DocumentSearch-EndDate', {
			dateFormat: 'Y-m-d',
			onReady: function(dateObj, dateStr, instance)
			{
				_Pict.providers.UtilityProvider.buildFlatpickrExtraButtons(instance, 'DocumentSearch-EndDate');
			},
			onChange: function(selectedDates, dateStr, instance)
			{
				// a date was selected, update the Start Date picker to disable dates after the end date
				const siblingElement = document.getElementById('DocumentSearch-StartDate');
				siblingElement['_flatpickr'].config.maxDate = dateStr ? dateStr : null;
				siblingElement['_flatpickr'].redraw();
			},
		});

		this.setCurrentSearchFilters();

		// if there is a callback function, call it
		if (pCallback)
		{
			pCallback();
		}
	}

	/**
	 * This method performs a search based on the current search filters.
	 * @param {Event} event - The event that triggered the search.
	 * @return {Promise<void>}
	 * @async
	 */
	async handleDocumentSearch(event)
	{
		// prevent the browser from refreshing on form submissions
		if (event)
		{
			event.preventDefault();
		}
		// add the loading spinner on the button when the action is being performed
		this.pict.ContentAssignment.addClass('#DocumentSearch-SearchButton', 'is-loading');
		// simulate that data is loading in the results table
		this.pict.views.HeadlightPanels.addLoadingSkeletonRows('DocumentSearch-Results-Container', this.CurrentSearchFilters.PageSize, '2.5rem');
		// collapse the filter panel to get out the way of the results
		this.pict.views.HeadlightPanels.toggleCollapsiblePanelBlockGroup('DocumentSearchFilters', 'close');
		// perform the search
		const tmpSearchResults = await this.pict.providers.DocumentProvider.performDocumentSearch(this.CurrentSearchFilters);
		// update the search results/UI based on the search results and total search count passed in
		this.updateWithSearchResults(tmpSearchResults.results, tmpSearchResults.count);
	}

	/**
	 * This function is used to handle the selection of a Document from the search results.
	 * @param {Event} event - The event that triggered the selection
	 * @param {string} pIDDocument - The ID of the Document that was selected
	 * @param {object} pContext - The context of the Document that was selected
	 * @return {void}
	 */
	handleDocumentSelection(event, pIDDocument, pContext)
	{
		if (!pIDDocument)
		{
			this.log.error('No IDDocument provided');
			return;
		}
		// figure out the context of the selection
		switch (pContext)
		{
		case 'Add':
			if (this.pict.views.DocumentSearch.DocumentSelectionCallback)
			{
				if (this.pict.views.DocumentSearch.CurrentSearchResults.length > 0)
				{
					const tmpDocument = this.pict.views.DocumentSearch.CurrentSearchResults.find((pDocument) => pDocument.IDDocument == pIDDocument);
					this.pict.views.DocumentSearch.documentSelectionCallback(tmpDocument);
					this.pict.views.HeadlightPanels.handlePanelToggle('Documents', 'close');
				}
			}
			break;
		case 'Details':
			this.pict.views.DocumentDetails.showDocumentDetailView(null, pIDDocument);
			break;
		case 'Add-With-Intent':
			this.pict.views.HeadlightPaneDocuments.Intent.handler(pIDDocument);
			break;
		case 'Inspection-Add':
			if (this.pict.AppData?.CurrentInspection?.IDInspection && pIDDocument)
			{
				this.pict.BridgeDocumentManager.addDocumentToInspection(pIDDocument, this.pict.AppData.CurrentInspection.IDInspection)
					.then((pResult) =>
					{
						// auto-add document to Dossier before switching back to Inspection view
						this.log.debug('auto-adding document to dossier as part of adding document to inspection');
						return this.pict.BridgeDocumentManager.includeDocumentInInspectionDossier(Number(pIDDocument), this.pict.AppData.CurrentInspection.IDInspection, true)
						.catch((pError) =>
						{
							this.log.error(`Error auto-adding document to dossier`, pError);
							this.pict.views.HeadlightPanels?.toast?.({
								message: `Error updating inspection dossier as part of adding document to inspection`,
								type: 'is-danger',
							});
						});
					})
					.then((pResult) =>
					{
						this.pict.views.Inspection.invalidate();
						// show a success message
						this.pict.views.HeadlightPanels.toast({
							message: 'The document has been added to the inspection.',
							type: 'is-success',
						});
						// TODO: understand why this doesn't for updating the popover from add/goto right after the initial add
						// update this currentSearchResults to reflect the document is now in the inspection
						const tmpDocument = this.pict.views.DocumentSearch.CurrentSearchResults.find((pDocument) => pDocument.IDDocument == pIDDocument);
						if (tmpDocument)
						{
							tmpDocument._InCurrentInspection = true;
							// re-render the search results to reflect the change
							this.pict.views.DocumentSearch.updateWithSearchResults(this.pict.views.DocumentSearch.CurrentSearchResults, this.pict.views.DocumentSearch.CurrentSearchFilters.TotalCount);
						}
					})
					.catch((pError) =>
					{
						this.log.error('Error adding document to inspection: ', pError);
						this.pict.views.HeadlightPanels?.toast?.({
							message: `<strong>Error adding document to inspection.</strong>`,
							type: 'is-danger',
						});
					});
			}
			else
			{
				this.pict.views.HeadlightPanels.handlePanelToggle('Inspection');
				this.setMode('none');
			}
			break;
		case 'Inspection-GoTo':
			// clear add mode so we don't accidentally add the document to the inspection again
			this.setMode('none');
			// TODO: pass the highlighted document to the inspection view so we scroll/highlight it
			this.pict.views.HeadlightPanels.handlePanelToggle('Inspection', 'open', () =>
			{
				// try to highlight the document in the inspection view
				this.pict.providers.UtilityProvider.highlightListItemRowElement('#DocumentRow_' + pIDDocument, '.nested-scroll-content');
			});
			break;
		default:
			this.log.error('Unknown context for handleDocumentSelection()!', { pContext });
		}
	}

	/**
	 * This method initializes the select2 plugin.
	 * @param {Object} pOptions - The options to use for initialization.
	 * @param {Function} pCallback - The callback function to execute after initialization.
	 * @return {void}
	 */
	initializeSelect2Plugin(pOptions, pCallback)
	{
		$('#' + pOptions.ElementID).select2({
			dropdownParent: $('#' + pOptions.ParentID),
			dropdownCssClass: pOptions.dropdownCssClass || 'theme-dark',
			/* @ts-ignore */
			selectionCssClass: pOptions.selectionCssClass || 'theme-dark',
			placeholder: pOptions.placeholder || 'Select...',
			minimumInputLength: pOptions.minimumInputLength > -1 ? pOptions.minimumInputLength : this.pict.views.HeadlightPanels.MinimumInputLengthForSearch,
			language: {
				searching: function()
				{
					return pOptions.searchingMessage || 'Searching...';
				},
				noResults: function()
				{
					return pOptions.noResultsMessage || 'No results found.';
				},
				loadingMore: function()
				{
					return pOptions.loadingMoreMessage || 'Loading more results...';
				},
				inputTooShort: function(args)
				{
					return pOptions.inputTooShortMessage || 'Please enter at least ' + args.minimum + ' characters to see results.';
				},
			},
			ajax: {
				xhrFields: {
					withCredentials: true,
				},
				dataType: 'json',
				method: 'GET',
				url: function(params)
				{
					const tmpSearchQuery = {
						filters: [],
						pagination: {
							start: pOptions?.CurrentPage || 1,
							limit: pOptions?.PageSize || 25,
						},
					};
					// add the search term to the query if it exists
					if (params.term)
					{
						tmpSearchQuery.filters.push({
							field: pOptions.TextField,
							comparator: 'LK',
							target: params.term,
						});
					}
					const startCount = (tmpSearchQuery.pagination.start - 1) * tmpSearchQuery.pagination.limit;
					const endCount = tmpSearchQuery.pagination.start * tmpSearchQuery.pagination.limit;
					return `/1.0/${pOptions.URL}/${_Pict.providers.UtilityProvider.generateMeadowFilter(tmpSearchQuery)}/${startCount}/${endCount}`;
				},
				processResults: function(data, params)
				{
					params.page = params.page || 1;

					const tmpSelections = _Pict.views.DocumentSearch.CurrentSearchFilters[pOptions.URL] || [];
					let formattedData = [];
					// if we have a custom format method, use it
					if (data && data.length > 0)
					{
						formattedData = pOptions.formatMethod ? pOptions.formatMethod(data) : data;
					}

					return {
						results: _Pict.providers.UtilityProvider.formatSelect2Results(formattedData, pOptions.IDField, pOptions.TextField, tmpSelections, pOptions.StickyOption),
						more: params.term ? data.length === _Pict.views.DocumentSearch.CurrentSearchFilters.PageSize : data.length === _Pict.views.DocumentSearch.CurrentSearchFilters.PageSize,
					};
				},
			},
		});
		// handle a preselected option if it exists
		if (pOptions.PreselectOption)
		{
			const select2 = $('#' + pOptions.ElementID);

			// create the option and append to Select2
			const option = new Option(pOptions.PreselectOption.text, pOptions.PreselectOption.id, true, true);

			select2.append(option).trigger('change');

			// manually trigger the `select2:select` event
			select2.trigger(
			{
				type: 'select2:select',
				params:
				{
					data: pOptions.PreselectOption,
				},
			});
		}
		// call the callback function if it exists
		if (pCallback)
		{
			pCallback();
		}
	}

	/**
	 * This method renders the previous page of search results.
	 * @return {void}
	 */
	renderPreviousPage()
	{
		if (this.CurrentSearchFilters.CurrentPage > 1)
		{
			this.CurrentSearchFilters.CurrentPage--;
		}
		else
		{
			this.CurrentSearchFilters.CurrentPage = 1;
		}
		this.pict.views.DocumentSearch.handleDocumentSearch();
	}

	/**
	 * This method renders the next page of search results.
	 * @return {void}
	 */
	renderNextPage()
	{
		if (this.CurrentSearchFilters.CurrentPage < this.CurrentSearchFilters.TotalPages)
		{
			this.CurrentSearchFilters.CurrentPage++;
		}
		else
		{
			this.CurrentSearchFilters.CurrentPage = this.CurrentSearchFilters.TotalPages;
		}
		this.pict.views.DocumentSearch.handleDocumentSearch();
	}

	/**
	 * This method resets the search filters to an empty state.
	 * @return {void}
	 */
	resetSearchFilters()
	{
		// reset the search filters to the default state
		this.CurrentSearchFilters = JSON.parse(JSON.stringify(this.DefaultSearchFilters));
		this.setCurrentSearchFiltersCount();
		this.renderAsync(() =>
		{
			this.log.info('Search filters reset');
		});
	}

	/**
	 * This method initializes the view and sets the default search filters.
	 * @param {object} pSearchFilters - The search filters to set.
	 * @return {void}
	 * @example
	 * setCurrentSearchFilters({Projects: [1234], Materials: [5678]})
	 */
	setCurrentSearchFilters(pSearchFilters)
	{
		// set the search filters to the passed in filters
		if (pSearchFilters)
		{
			this.CurrentSearchFilters = Object.assign(JSON.parse(JSON.stringify(this.DefaultSearchFilters)), pSearchFilters);
		}
		// if the search entry form exists (is actively the rendered panel), stuff the values into the form
		if (document.getElementById('DocumentSearch-SearchEntry'))
		{
			// convert the date strings to just the date part (we expect ISO strings)
			const tmpStartDate = this.CurrentSearchFilters?.StartDate ? this.CurrentSearchFilters?.StartDate.split('T')[0] : '';
			const tmpEndDate = this.CurrentSearchFilters?.EndDate ? this.CurrentSearchFilters?.EndDate.split('T')[0] : '';
			// for each filter, also stuff the values into the search form.
			this.pict.ContentAssignment.assignContent('#DocumentSearch-CurrentPage', this.CurrentSearchFilters?.CurrentPage || 1);
			this.pict.ContentAssignment.assignContent('#DocumentSearch-PageSize', this.CurrentSearchFilters?.PageSize || 25);
			this.pict.ContentAssignment.assignContent('#DocumentSearch-Projects', this.CurrentSearchFilters?.Projects);
			this.pict.ContentAssignment.assignContent('#DocumentSearch-StartDate', tmpStartDate);
			this.pict.ContentAssignment.assignContent('#DocumentSearch-EndDate', tmpEndDate);
			this.pict.ContentAssignment.assignContent('#DocumentSearch-SortBy', `${this.CurrentSearchFilters?.SortBy}_${this.CurrentSearchFilters?.SortDirection}`);
		}
		this.setCurrentSearchFiltersCount();
	}

	/**
	 * This method sets the current search filters count on the 'Set Filters' button.
	 * @return {void}
	 */
	setCurrentSearchFiltersCount()
	{
		this.CurrentSearchFiltersCount = 0;
		const my = this.CurrentSearchFilters;

		if (my.Projects && my.Projects.length > 0)
		{
			this.CurrentSearchFiltersCount += my.Projects.length;
		}
		if (my.StartDate)
		{
			this.CurrentSearchFiltersCount += 1;
		}
		if (my.EndDate)
		{
			this.CurrentSearchFiltersCount += 1;
		}
	}

	/**
	 * Set the callback function for the search results.
	 * @param {Function} pCallback - The callback function to set.
	 * @return {void}
	 * @example
	 * setDocumentSearchResultsCallback((pResults) => { console.log(pResults); });
	 */
	setDocumentSearchResultsCallback(pCallback)
	{
		// TODO: not sure if this is the best way to handle this, but it works for now.
		this.DocumentSearchResultsCallback = pCallback;
	}


	/**
	 * Updates the search filter value based on the event and filter type.
	 * @param {string} pFilterType - The type of filter to update.
	 * @return {void}
	 * @example
	 * updateSearchFilterValue('Projects');
	 */
	updateSearchFilterValue(pFilterType)
	{
		switch (pFilterType)
		{
		case 'Project':
		case 'Projects':
			this.CurrentSearchFilters.Projects = this.pict.ContentAssignment.readContent('#DocumentSearch-Projects');
			break;
		case 'User':
		case 'Users':
			this.CurrentSearchFilters.Users = this.pict.ContentAssignment.readContent('#DocumentSearch-Users');
			break;
		case 'BeginDate':
		case 'StartDate':
			const tmpStartDate = this.pict.ContentAssignment.readContent('#DocumentSearch-StartDate');
			this.CurrentSearchFilters.StartDate = tmpStartDate ? tmpStartDate + 'T00:00:00.000Z' : '';
			break;
		case 'EndDate':
		case 'StopDate':
			const tmpEndDate = this.pict.ContentAssignment.readContent('#DocumentSearch-EndDate');
			this.CurrentSearchFilters.EndDate = tmpEndDate ? tmpEndDate + 'T23:59:59.000Z' : '';
			break;
		case 'SearchText':
			this.CurrentSearchFilters.SearchText = this.pict.ContentAssignment.readContent('#DocumentSearch-SearchText');
			break;
		case 'Status':
			this.CurrentSearchFilters.Status = this.pict.ContentAssignment.readContent('#DocumentSearch-Status');
			break;
		case 'SortBy':
			const tempSortBy = this.pict.ContentAssignment.readContent('#DocumentSearch-SortBy');
			if (typeof tempSortBy === 'string' && tempSortBy != null)
			{
				this.CurrentSearchFilters.SortBy = tempSortBy.split('_')[0];
				this.CurrentSearchFilters.SortDirection = tempSortBy.split('_')[1];
			}
			else
			{
				this.CurrentSearchFilters.SortBy = 'DocumentDate';
				this.CurrentSearchFilters.SortDirection = 'DESC';
			}
			break;
		default:
			this.log.error(`Unknown filter type: ${pFilterType}`);
		}
		this.setCurrentSearchFiltersCount();
	}

	/**
	 * This method updates the search results/UI based on the search results and total search count passed in.
	 * @param {Array} pSearchResults - The search results to update with.
	 * @param {number} pTotalSearchCount - The total search count to update with.
	 * @return {Promise<void>}
	 */
	async updateWithSearchResults(pSearchResults, pTotalSearchCount)
	{
		// update our results list
		this.CurrentSearchResults = pSearchResults;
		// if the mode is 'add-to-inspection', we need to look up if the documents are already in the inspection
		if (this.mode === 'add-to-inspection')
		{
			// if we have an inspection, check if the documents are already in the inspection
			if (this.pict.AppData?.CurrentInspection?.IDInspection)
			{
				await this.pict.BridgeDocumentManager.getListOfCurrentInspectionIDDocuments(this.pict.AppData.CurrentInspection.IDInspection)
					.then((pInspectionDocuments) =>
					{
						// loop through the search results and mark the documents that are already in the inspection
						this.CurrentSearchResults.forEach((pDocument) =>
						{
							// if the document is in the inspection, mark it as such
							if (pInspectionDocuments.find((pInspectionDocument) => pInspectionDocument == pDocument.IDDocument))
							{
								pDocument._InCurrentInspection = true;
							}
							else
							{
								pDocument._InCurrentInspection = false;
							}
						});
					})
					.catch((pError) =>
					{
						this.log.error('Error getting inspection documents for add-to-inspection mode', pError);
					});
			}
			else
			{
				this.log.warn('No current inspection found for add-to-inspection mode, skipping inspection document check.');
			}
		}
		// update the total possible count so we can calculate the total pages
		this.CurrentSearchFilters.TotalPages = Math.ceil(pTotalSearchCount / this.CurrentSearchFilters.PageSize);
		// render the search results view
		this.pict.views.DocumentSearch.renderAsync('DocumentSearch-Results', '#DocumentSearch-Results-Container', false, false, () =>
		{
			// remove the loading spinner on the button when the action has been completed
			this.pict.ContentAssignment.removeClass('#DocumentSearch-SearchButton', 'is-loading');
			// render the pagination buttons
			this.pict.views.DocumentSearch.renderAsync('DocumentSearch-Pagination', '#DocumentSearch-Pagination-Container', false, false, () =>
			{
				this.log.info('Document Search Pagination rendered');
			});
			// if there is a callback function, call it
			if (this.documentSearchResultsCallback)
			{
				this.documentSearchResultsCallback(this.CurrentSearchResults);
			};
		});
	}

	/**
	 * This method handles the action popover for the observation search results.
	 * @param {string} pElementId - The ID of the element to attach the popover to.
	 * @param {number} IDDocument - The ID of the document.
	 */
	handleActionPopover(pElementId, IDDocument)
	{
		const tmpMenuElement = document.getElementById(pElementId);
		if (!tmpMenuElement)
		{
			this.pict.log.error(`Element with ID ${pElementId} not found for Observation search rults action popover.`);
			return;
		}
		// create the popover menu
		this.fable.HeadlightPopover.createPopover({
			PopoverDestinationAddress: pElementId,
			PopoverTemplateHash: 'DocumentSearch-Results-Popover-Menu',
			PopoverTemplateRecord:
			{
				IDDocument: IDDocument,
				InCurrentInspection: this.CurrentSearchResults.find((pDocument) => pDocument.IDDocument == IDDocument)?._InCurrentInspection || false,
			},
			theme: 'dropdown-dark',
		});
	}
}

module.exports = DocumentSearch;

module.exports.default_configuration =
{
	ViewIdentifier: 'DocumentSearch',
	DefaultRenderable: 'DocumentSearch-Entry',
	DefaultDestinationAddress: '#DocumentSearch-Entry-Container',
	CSS: /*css*/`
		.mode_add-to-inspection {
			display: none;
		}
		.visible-mode_add-to-inspection .mode_add-to-inspection {
			display: block;
		}
		.mode_add-with-intent {
			display: none;
		}
		.visible-mode_add-with-intent .mode_add-with-intent {
			display: block;
		}
		`,
	Templates:
		[
			{
				Hash: 'DocumentSearch-Entry',
				Template: /*html*/`
				<div id="DocumentSearch-SearchEntry" class="container">
					<form autocomplete="off" role="Document-search" onsubmit="_Pict.views.DocumentSearch.handleDocumentSearch(event);">
						<div class="px-4 pt-4" data-collapsible-content="DocumentSearchFilters">
							<div class="columns mb-0 is-hidden">
								{~T:DocumentSearch-Entry-CurrentPage~}
								{~T:DocumentSearch-Entry-PageSize~}
							</div>
							<div class="columns mb-0">
								{~T:DocumentSearch-Entry-ProjectSelector~}
							</div>
							<div class="columns mb-0">
								{~T:DocumentSearch-Entry-SearchText~}
								{~T:DocumentSearch-Entry-Status~}
							</div>
							<div class="columns mb-0">
								{~T:DocumentSearch-Entry-UserSelector~}
								{~T:DocumentSearch-Entry-SortBy~}
							</div>
							<div class="columns mb-0">
								{~T:DocumentSearch-Entry-StartDate~}
								{~T:DocumentSearch-Entry-EndDate~}
							</div>
						</div>
						<div class="buttons p-4 is-right" data-theme="dark">
							<div id="DocumentSearch-Pagination-Container" style="flex:auto"></div>
							<button 
								id="DocumentSearch-ToggleButton"
								type="button"
								title="Set filters"
								class="button is-primary is-soft" 
								data-collapsible-toggle="DocumentSearchFilters" 
								onclick="_Pict.views.HeadlightPanels.toggleCollapsiblePanelBlockGroup('DocumentSearchFilters')"
							>
								<span class="icon">
									<i class="fas fa-fw fa-filter"></i>
								</span>
								<span>Set Filters</span>
								<!-- hide the badge until we fix the count to be accurate -->
								<span id="DocumentSearch-Entry-ActiveFilterCount" class="is-hidden badge is-warning">{~T:DocumentSearch-Entry-ActiveFilterCount~}</span>
							</button>
							<button class="button is-primary is-soft" type="button" id="DocumentSearch-ResetButton" title="Reset filters" onclick="_Pict.views.DocumentSearch.resetSearchFilters()">
								<i class="fas fa-fw fa-undo mr-1"></i> 
								Clear Filters
							</button>
							<button class="button is-primary" type="submit" id="DocumentSearch-SearchButton" title="Search documents">
								<i class="fas fa-fw fa-search mr-1"></i> 
								Search Documents
							</button>
						</div>
					</form>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Entry-ActiveFilterCount',
				Template: /*html*/`{~D:Context[0].CurrentSearchFiltersCount~}`,
			},
			{
				Hash: 'DocumentSearch-Entry-CurrentPage',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label" for="DocumentSearch-CurrentPage" class="block">Current Page</label>
						<input 
							id="DocumentSearch-CurrentPage" 
							type="number" 
							class="input" 
							placeholder="1" 
							onchange="_Pict.views.DocumentSearch.updateSearchFilterValue('CurrentPage')" 
							value="" 
						/>
					</div>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Entry-PageSize',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label" for="DocumentSearch-PageSize" class="block">Page Size</label>
						<input 
							id="DocumentSearch-PageSize" 
							type="number" 
							class="input" 
							placeholder="50" 
							onchange="_Pict.views.DocumentSearch.updateSearchFilterValue('PageSize')" 
							value="" 
						/>
					</div>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Entry-ProjectSelector',
				Template: /*html*/`
				<div class="column">
					<div class="field" style="margin-bottom: 10px;">
						<label class="label block" for="DocumentSearch-Projects">Projects</label>
						<div class="control select is-fullwidth">
							<select 
								id="DocumentSearch-Projects" 
								onchange="_Pict.views.DocumentSearch.updateSearchFilterValue('Projects')"
								multiple
							>{~TS:DocumentSearch-Entry-ProjectSelector-SelectedOptions:Context[0].CurrentSearchFilters.Projects~}</select>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Enty-ProjectSelector-SelectedOptions',
				Template: /*html*/`<option value="{~D:Record.IDProject~}" selected>{~D:Record.Name~}</option>`,
			},
			{
				Hash: 'DocumentSearch-Entry-UserSelector',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label block" for="DocumentSearch-Users">Users</label>
						<div class="control select is-fullwidth">
							<select 
								id="DocumentSearch-Users" 
								onchange="_Pict.views.DocumentSearch.updateSearchFilterValue('Users')"
								multiple
							>{~TS:DocumentSearch-Entry-UserSelector-SelectedOptions:Context[0].CurrentSearchFilters.Users~}</select>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Entry-SearchText',
				Template: /*html*/`
				<div class="column">
					<label class="label" for="DocumentSearch-SearchText" class="block">Text Search</label>
					<input 
						id="DocumentSearch-SearchText"
						type="text"
						class="input" 
						placeholder="Enter any specific text to search"
						onchange="_Pict.views.DocumentSearch.updateSearchFilterValue('SearchText')" 
						value=""
					/>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Entry-Status',
				Template: /*html*/`
				<div class="column">
					<label class="label" for="DocumentSearch-Status" class="block">Status</label>
					<input 
						id="DocumentSearch-Status"
						type="text"
						class="input" 
						placeholder="Enter a status to search"
						onchange="_Pict.views.DocumentSearch.updateSearchFilterValue('Status')" 
						value=""
					/>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Enty-UserSelector-SelectedOptions',
				Template: /*html*/`<option value="{~D:Record.IDUser~}" selected>{~D:Record.Name~}</option>`,
			},
			{
				Hash: 'DocumentSearch-Entry-StartDate',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label" for="DocumentSearch-StartDate" class="block">Start Date</label>
						<div class="control has-icons-left">
							<input 
								id="DocumentSearch-StartDate" 
								type="date" 
								class="input" 
								placeholder="Search by Start Date..." 
								onchange="_Pict.views.DocumentSearch.updateSearchFilterValue('StartDate')"
								value="" 
							/>
							<span class="icon is-small is-left">
								<i class="fas fa-calendar fa-xs"></i>
							</span>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Entry-EndDate',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label" for="DocumentSearch-EndDate" class="block">End Date</label>
						<div class="control has-icons-left">
							<input 
								id="DocumentSearch-EndDate" 
								type="date" 
								class="input" 
								placeholder="Search by End Date..." 
								onchange="_Pict.views.DocumentSearch.updateSearchFilterValue('EndDate')"
								value="" 
							/>
							<span class="icon is-small is-left">
								<i class="fas fa-calendar fa-xs"></i>
							</span>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Entry-SortBy',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label" for="DocumentSearch-SortBy" class="block">Sort By</label>
						<div class="control select is-fullwidth">
							<select 
								id="DocumentSearch-SortBy" 
								type="date" 
								class="input" 
								placeholder="Search by End Date..." 
								onchange="_Pict.views.DocumentSearch.updateSearchFilterValue('SortBy')"
								value="" 
							>
								<option value="DocumentDate_DESC">Document Date (Newest)</option>
								<option value="DocumentDate_ASC">Document Date (Oldest)</option>
								<option value="Status_ASC">Status (A-Z)</option>
								<option value="Status_DESC">Status (Z-A)</option>
							</select>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Results',
				Template: /*html*/`
				<div id="DocumentSearch-Results-Container" class="container table-container">
					{~TIfAbs:DocumentSearch-Results-Empty:Context[0]:Context[0].CurrentSearchResults.length^==^0~}
					{~TIfAbs:DocumentSearch-Results-Table:Context[0]:Context[0].CurrentSearchResults.length^>^0~}
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Results-Table',
				Template: /*html*/`
				<table class="table is-striped is-hoverable is-fullwidth has-sticky-table-header">
					<thead>
						<tr>
							<th style="min-width: 115px">Document Date</th>
							<th>Project</th>
							<th>Name</th>
							<th>User</th>
							<th class="has-text-centered">Status</th>
							<th class="has-text-centered is-vcentered">Actions</th>
						</tr>
					</thead>
					<tbody id="DocumentSearch-Results-Entries">
						{~TS:DocumentSearch-Results-Row:Record.CurrentSearchResults~}
					</tbody>
				</table>`,
			},
			{
				Hash: 'DocumentSearch-Results-Row',
				Template: /*html*/`
				<tr>
					<td>{~DateFormat:Record.DocumentDate^YYYY-MM-DD~}</td>
					<td>
						{~TIfAbs:DocumentSearch-Results-EntityValue-Project:Record:Record.IDProject^>^0~}
						{~TIfAbs:DocumentSearch-Results-EntityValue-NA:Record:Record.IDProject^<=^0~}
					</td>
					<td>
						{~TIfAbs:DocumentSearch-Results-DatumValue-Name:Record:Record.Name^>^0~}
						{~TIfAbs:DocumentSearch-Results-DatumValue-NA:Record:Record.Name^<=^0~}
					</td>
					<td>
						{~TIfAbs:DocumentSearch-Results-EntityValue-User:Record:Record.CreatingIDUser^>^0~}
						{~TIfAbs:DocumentSearch-Results-EntityValue-NA:Record:Record.CreatingIDUser^<=^0~}
					</td>
					<td>
						{~TIfAbs:DocumentSearch-Results-DatumValue-Status:Record:Record.Status^>^0~}
						{~TIfAbs:DocumentSearch-Results-DatumValue-NA:Record:Record.Status^<=^0~}
					</td>
					<td class="has-text-centered is-vcentered" id="DocumentSearch-Results-Popover-TriggerButton-{~D:Record.IDDocument~}">
						{~T:DocumentSearch-Results-Popover-TriggerButton:Record~}
					</td>
				</tr>`,
			},
			{
				Hash: 'DocumentSearch-Results-Popover-TriggerButton',
				Template: /*html*/`
				<button 
					id="DocumentSearch-DetailsButton-{~D:Record.IDDocument~}"
					type="button" 
					class="button is-ghost has-text-primary-75 p-0" 
					title="Actions" 
					onclick="_Pict.views.DocumentSearch.handleActionPopover(id, '{~D:Record.IDDocument~}')"
					onmouseover="_Pict.views.DocumentSearch.handleActionPopover(id, '{~D:Record.IDDocument~}')">
					<span class="icon">
						<i class="fas fa-fw fa-lg fa-circle-ellipsis"></i>
					</span>
				</button>`,
			},
			{
				Hash: 'DocumentSearch-Results-Popover-Menu',
				Template: /*html*/`
				<ul class="list-menu visible-mode_{~D:Pict.views.DocumentSearch.mode~}">
					<li class="list-menu-item mode_add-with-intent {~Hide:Record.InCurrentInspection~}">
						<a onclick="_Pict.views.DocumentSearch.handleDocumentSelection(event, '{~D:Record.IDDocument~}', 'Add-With-Intent');_Pict.fable.HeadlightPopover.hideAll();">
							<span class="icon mr-1">
								<i class="fa fa-fw fa-plus-circle"></i>
							</span>
							<span class="icon-text">Add</span>
						</a>
					</li>
					<li class="list-menu-item mode_add-to-inspection {~Hide:Record.InCurrentInspection~}">
						<a onclick="_Pict.views.DocumentSearch.handleDocumentSelection(event, '{~D:Record.IDDocument~}', 'Inspection-Add');_Pict.fable.HeadlightPopover.hideAll();">
							<span class="icon mr-1">
								<i class="fa fa-fw fa-plus-circle"></i>
							</span>
							<span class="icon-text">Add to Inspection</span>
						</a>
					</li>
					<li class="list-menu-item mode_add-to-inspection {~Show:Record.InCurrentInspection~}">
						<a onclick="_Pict.views.DocumentSearch.handleDocumentSelection(event, '{~D:Record.IDDocument~}', 'Inspection-GoTo');_Pict.fable.HeadlightPopover.hideAll();">
							<span class="icon mr-1">
								<i class="fa fa-fw fa-building-magnifying-glass"></i>
							</span>
							<span class="icon-text">Go to Inspection</span>
						</a>
					</li>
					<li class="list-menu-item">
						<a onclick="_Pict.views.DocumentSearch.handleDocumentSelection(event, '{~D:Record.IDDocument~}', 'Details');_Pict.fable.HeadlightPopover.hideAll();">
							<span class="icon mr-1">
								<i class="fa fa-fw fa-memo"></i>
							</span>
							<span class="icon-text">View Details</span>
						</a>
					</li>
				</ul>
				`,
			},
			{
				Hash: 'DocumentSearch-Results-EntityValue-NA',
				Template: /*html*/`<span class="has-text-text-50">N/A</span>`,
			},
			{
				Hash: 'DocumentSearch-Results-DatumValue-NA',
				Template: /*html*/`<span class="has-text-text-50">N/A</span>`,
			},
			{
				Hash: 'DocumentSearch-Results-EntityValue-Project',
				Template: /*html*/`{~Entity:Project^Record.IDProject^DocumentSearch-Results-Project^~}`,
			},
			{
				Hash: 'DocumentSearch-Results-EntityValue-User',
				Template: /*html*/`{~Entity:User^Record.CreatingIDUser^DocumentSearch-Results-User^~}`,
			},
			{
				Hash: 'DocumentSearch-Results-DatumValue-Name',
				Template: /*html*/`{~D:Record.Name~}`,
			},
			{
				Hash: 'DocumentSearch-Results-DatumValue-Status',
				Template: /*html*/`{~DocumentState:Record~}`,
			},
			{
				Hash: 'DocumentSearch-Results-Empty',
				Template: /*html*/`
				<div class="notification m-3 empty-message">
					<p class="has-text-centered">
						<span class="icon is-large">
							<i class="fas fa-search"></i>
						</span>
						<span>We could not find any Documents based on the current search criteria.</span>
					</p>
				</div>`,
			},
			{
				Hash: 'DocumentSearch-Results-Project',
				Template: /*html*/`{~D:Record.Name~}`,
			},
			{
				Hash: 'DocumentSearch-Results-User',
				Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
			},
			{
				Hash: 'DocumentSearch-Pagination',
				Template: /*html*/`
				<nav id="DocumentSearch-Pagination" class="pagination" style="justify-content: flex-start;" role="navigation" aria-label="pagination">
					<button id="DocumentSearch-PreviousButton" type="button" class="button is-soft mr-2" title="Previous page" onclick="_Pict.views.DocumentSearch.renderPreviousPage();">
						<span class="icon">
							<i class="fas fa-fw fa-chevron-left"></i> 
						</span>
						<span>Previous</span>
					</button>
					<button id="DocumentSearch-NextButton" type="button" class="button is-soft" title="Next page" onclick="_Pict.views.DocumentSearch.renderNextPage();">
						<span>Next</span> 
						<span class="icon">
							<i class="fas fa-fw fa-chevron-right ml-1"></i>
						</span>
					</button>
				</nav>`,
			},
		],
	Renderables:
		[
			{
				RenderableHash: 'DocumentSearch-Entry',
				TemplateHash: 'DocumentSearch-Entry',
				DestinationAddress: '#DocumentSearch-Entry-Container',
			},
			{
				RenderableHash: 'DocumentSearch-Entry-ProjectSelector',
				TemplateHash: 'DocumentSearch-Entry-ProjectSelector',
				DestinationAddress: '#DocumentSearch-Entry-ProjectSelector',
			},
			{
				RenderableHash: 'DocumentSearch-Entry-MaterialSelector',
				TemplateHash: 'DocumentSearch-Entry-MaterialSelector',
				DestinationAddress: '#DocumentSearch-Entry-MaterialSelector',
			},
			{
				RenderableHash: 'DocumentSearch-Entry-MixDesignSelector',
				TemplateHash: 'DocumentSearch-Entry-MixDesignSelector',
				DestinationAddress: '#DocumentSearch-Entry-MixDesignSelector',
			},
			{
				RenderableHash: 'DocumentSearch-Entry-ActiveFilterCount',
				TemplateHash: 'DocumentSearch-Entry-ActiveFilterCount',
				DestinationAddress: '#DocumentSearch-Entry-ActiveFilterCount',
			},
			{
				RenderableHash: 'DocumentSearch-Results',
				TemplateHash: 'DocumentSearch-Results',
				DestinationAddress: '#DocumentSearch-Results-Container',
			},
			{
				RenderableHash: 'DocumentSearch-Pagination',
				TemplateHash: 'DocumentSearch-Pagination',
				DestinationAddress: '#DocumentSearch-Pagination-Container',
			},
			{
				RenderableHash: 'DocumentSearch-Results-Popover-TriggerButton',
				TemplateHash: 'DocumentSearch-Results-Popover-TriggerButton',
				DestinationAddress: '#DocumentSearch-Results-Popover-TriggerButton-{~D:Record.IDDocument~}',
			},
		],
};
